import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const StripeContext = createContext(null);

export const useStripe = () => {
  return useContext(StripeContext);
};

export const useStripeContext = () => {
  return useContext(StripeContext);
};

let stripePromiseInstance = null;

const getStripePromise = (publishableKey) => {
  if (!stripePromiseInstance && publishableKey) {
    stripePromiseInstance = loadStripe(publishableKey);
  }
  return stripePromiseInstance;
};

export const StripeProvider = ({ children }) => {
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);

  const publishableKey = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;

  useEffect(() => {
    const initStripe = async () => {
      try {
        if (!publishableKey || publishableKey?.includes('your-') || publishableKey?.includes('placeholder')) {
          setStripeError('Stripe not configured');
          setLoading(false);
          return;
        }

        const promise = getStripePromise(publishableKey);
        setStripePromise(promise);

        const stripe = await promise;
        if (stripe) {
          setStripeLoaded(true);
        } else {
          setStripeError('Failed to initialize Stripe');
        }
      } catch (err) {
        console.error('Stripe initialization error:', err);
        setStripeError('Stripe initialization failed');
      } finally {
        setLoading(false);
      }
    };

    initStripe();
  }, [publishableKey]);

  /**
   * Create a Stripe Checkout Session via the Supabase edge function
   * and redirect the user to the hosted Stripe Checkout page.
   */
  const createCheckoutSession = async ({ priceId, userId, successUrl, cancelUrl, planName, amount }) => {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      // Get auth token if available
      let token = supabaseAnonKey || '';
      try {
        const { supabase } = await import('../lib/supabase');
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.access_token) token = session?.access_token;
      } catch (e) {}

      // If no priceId from DB, fall back to a direct payment intent approach
      // by redirecting to the subscription management page with plan info
      if (!priceId) {
        // No Stripe price ID configured — use direct card payment flow
        return { url: null, error: 'no_price_id' };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabaseAnonKey || ''
        },
        body: JSON.stringify({
          priceId,
          userId,
          successUrl: successUrl || `${window.location?.origin}/subscription-management?success=true`,
          cancelUrl: cancelUrl || `${window.location?.origin}/subscription-management`,
          planName,
          amount
        })
      });

      if (!response?.ok) {
        const errorText = await response?.text()?.catch(() => 'Unknown error');
        throw new Error(`Checkout session failed: ${errorText}`);
      }

      const data = await response?.json();
      return { url: data?.url, sessionId: data?.sessionId };
    } catch (err) {
      console.error('createCheckoutSession error:', err);
      return { url: null, error: err?.message };
    }
  };

  const stripeOptions = {
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#2563eb',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  };

  const value = {
    stripeLoaded,
    stripeError,
    stripePromise,
    stripeOptions,
    loading,
    error: stripeError,
    publishableKey: publishableKey || null,
    createCheckoutSession,
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
};

export default StripeContext;